import { Box } from '@interstate/components/Box';
import { SelectInput } from '@interstate/components/SelectInput';
import { TextInput } from '@makemydeal/dr-shared-components/text-input';
import { Typography } from '@interstate/components/Typography';
import {
    CO_APPLICANT_TYPE_OPTIONS,
    MARITAL_STATUS_OPTIONS,
    RELATIONSHIP_TO_THE_BUYER_OPTIONS,
    SUFFIX_OPTIONS,
    TITLE_OPTIONS
} from '../../constants';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { useCustomerCardContext } from '../CustomerFormContext';
import { FieldsRow, SummaryFieldsRow } from './sectionLayout.styles';
import ChangeCustomerCta from '../ChangeCustomerCta';

/**
 * Renders the Personal Information field group inside CustomerForm.
 * Reads all state and handlers from CustomerFormContext — accepts no props.
 *
 * Summary mode: firstName, lastName (2-column grid, no section heading)
 * Full mode:    Personal Information heading + firstName, middleName, lastName,
 *               title, suffix, maritalStatus (4-column grid)
 *
 * Optional desking: required fields show "(required)" in the label instead of the asterisk.
 */
const PersonalInfoFields = () => {
    const {
        formValues,
        formErrors,
        handleInputChange,
        isLocked,
        viewMode,
        customerRole,
        isOptionalDeskingActive,
        isBuyerBusiness,
        isCurrentCustomerBusiness,
        isCreditAppFieldsCoverageEnabled,
        personalInfoHeaderAction
    } = useCustomerCardContext();

    // TODO: Verify expected behaviour with design/QA.
    // Interstate's TextInput renders BOTH a "(required)" text label AND an asterisk "*" when
    // required={true}. This means in default (non-optional-desking) mode the label reads
    // "Legal First Name (required) *". In optional-desking mode required={false}, so Interstate
    // adds neither — instead we inject "(required)" via requiredLabel to keep the label consistent.
    // Confirm this double-indicator in default mode is intentional, or if required={true} should
    // be dropped in favour of requiredLabel alone.
    const requiredLabel = (base: string) => (isOptionalDeskingActive ? `${base} (required)` : base);

    const renderFirstName = () => (
        <TextInput
            label={requiredLabel('Legal First Name')}
            name="firstName"
            data-testid={`customer-form-${customerRole}-first-name`}
            className="pii"
            value={formValues.firstName ?? ''}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            disabled={isLocked}
            hasError={formErrors?.firstName?.hasError}
            errorMessage={formErrors?.firstName?.errorMessage}
            selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
            required={!isOptionalDeskingActive}
            displayLabel
            size="small"
        />
    );

    const renderLastName = () => (
        <TextInput
            label={requiredLabel('Legal Last Name')}
            name="lastName"
            data-testid={`customer-form-${customerRole}-last-name`}
            className="pii"
            value={formValues.lastName ?? ''}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            disabled={isLocked}
            hasError={formErrors?.lastName?.hasError}
            errorMessage={formErrors?.lastName?.errorMessage}
            selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
            required={!isOptionalDeskingActive}
            displayLabel
            size="small"
        />
    );

    if (viewMode === 'summary') {
        return (
            <SummaryFieldsRow>
                {renderFirstName()}
                {renderLastName()}
            </SummaryFieldsRow>
        );
    }

    return (
        <>
            {!isCurrentCustomerBusiness && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', marginTop: '1.5rem' }}>
                    <Typography variant="h5">Personal Information</Typography>
                    {customerRole === 'buyer' && personalInfoHeaderAction}
                    <ChangeCustomerCta customerRole={customerRole} isLocked={isLocked} />
                </Box>
            )}
            <FieldsRow>
                {isCreditAppFieldsCoverageEnabled && customerRole === 'coBuyer' && isBuyerBusiness && (
                    <SelectInput
                        label="Co-Applicant Type"
                        name="participantRole"
                        data-testid={`customer-form-${customerRole}-co-applicant-type`}
                        value={formValues.participantRole || ''}
                        onChange={(e) => handleInputChange('participantRole', e.target.value || '')}
                        options={CO_APPLICANT_TYPE_OPTIONS}
                        disabled={isLocked}
                        displayLabel
                        size="small"
                    />
                )}
                {renderFirstName()}
                <TextInput
                    label="Legal Middle Name"
                    name="middleName"
                    data-testid={`customer-form-${customerRole}-middle-name`}
                    className="pii"
                    value={formValues.middleName ?? ''}
                    onChange={(e) => handleInputChange('middleName', e.target.value)}
                    disabled={isLocked}
                    hasError={formErrors?.middleName?.hasError}
                    errorMessage={formErrors?.middleName?.errorMessage}
                    selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                    displayLabel
                    size="small"
                />
                {renderLastName()}
                {isCurrentCustomerBusiness && customerRole === 'buyer' ? (
                    <TextInput
                        label="Title"
                        name="title"
                        data-testid={`customer-form-${customerRole}-title`}
                        value={formValues.title || ''}
                        onChange={(e) => handleInputChange('title', e.target.value || '')}
                        hasError={formErrors?.title?.hasError}
                        errorMessage={formErrors?.title?.errorMessage}
                        selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                        disabled={isLocked}
                        displayLabel
                        size="small"
                    />
                ) : (
                    <SelectInput
                        label="Title"
                        name="title"
                        data-testid={`customer-form-${customerRole}-title`}
                        value={formValues.title || ''}
                        onChange={(e) => handleInputChange('title', e.target.value || '')}
                        options={TITLE_OPTIONS}
                        disabled={isLocked}
                        displayLabel
                        size="small"
                    />
                )}
                <SelectInput
                    label="Suffix"
                    name="suffix"
                    data-testid={`customer-form-${customerRole}-suffix`}
                    value={formValues.suffix || ''}
                    onChange={(e) => handleInputChange('suffix', e.target.value || '')}
                    options={SUFFIX_OPTIONS}
                    disabled={isLocked}
                    displayLabel
                    size="small"
                />
                {isCreditAppFieldsCoverageEnabled && !isCurrentCustomerBusiness && (
                    <SelectInput
                        label="Marital Status"
                        name="maritalStatus"
                        data-testid={`customer-form-${customerRole}-marital-status`}
                        value={formValues.maritalStatus || ''}
                        onChange={(e) => handleInputChange('maritalStatus', e.target.value || '')}
                        options={MARITAL_STATUS_OPTIONS}
                        disabled={isLocked}
                        displayLabel
                        size="small"
                    />
                )}
                {isCreditAppFieldsCoverageEnabled && customerRole === 'coBuyer' && !isBuyerBusiness && (
                    <SelectInput
                        label="Relationship to the Buyer"
                        name="relationshipToBuyer"
                        data-testid={`customer-form-${customerRole}-relationship-to-buyer`}
                        value={formValues.relationshipToBuyer || ''}
                        onChange={(e) => handleInputChange('relationshipToBuyer', e.target.value || '')}
                        options={RELATIONSHIP_TO_THE_BUYER_OPTIONS}
                        disabled={isLocked}
                        displayLabel
                        size="small"
                    />
                )}
            </FieldsRow>
        </>
    );
};

PersonalInfoFields.displayName = 'PersonalInfoFields';

export default PersonalInfoFields;
